import pickBy from 'lodash/pickBy';
import {space} from '../utils/space';

/**
 * Based on theme specifications/API outline by Styled System:
 * https://styled-system.com/api/
 *
 * - Space
 * - Color (Omitted, for now)
 * - Typography (Omitted, for now)
 * - Layout
 * - Flexbox
 * - Grid Layout
 * - Background (Omitted, for now)
 * - Border (Omitted, for now)
 * - Position
 * - Shadow (Omitted, for now)
 */

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/margin
 */
export const margin = ({
  // (New) Preferred
  marginInlineStart,

  marginInlineEnd,
  marginInline,
  marginBlock,
  marginBlockStart,
  marginBlockEnd,

  // (Old) Standard
  marginLeft,

  marginRight,
  marginTop,
  marginBottom,

  // Deprecated
  marginX,

  marginY,
  mx,
  my,

  // All
  margin: all,
}: any) => {
  return {
    margin: space(all),
    marginBlock: space(marginBlock || marginY || my),
    marginBlockEnd: space(marginBlockEnd || marginBottom),
    marginBlockStart: space(marginBlockStart || marginTop),
    marginInline: space(marginInline || marginX || mx),
    marginInlineEnd: space(marginInlineEnd || marginRight),
    marginInlineStart: space(marginInlineStart || marginLeft),
  };
};

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/padding
 */
export const padding = ({
  // (New) Preferred
  paddingInlineStart,

  paddingInlineEnd,
  paddingInline,
  paddingBlock,
  paddingBlockStart,
  paddingBlockEnd,

  // (Old) Standard
  paddingLeft,

  paddingRight,
  paddingTop,
  paddingBottom,

  // Deprecated
  paddingX,

  paddingY,
  mx,
  my,

  // All
  padding: all,
}: any) => {
  return {
    padding: space(all),
    paddingBlock: space(paddingBlock || paddingY || my),
    paddingBlockEnd: space(paddingBlockEnd || paddingBottom),
    paddingBlockStart: space(paddingBlockStart || paddingTop),
    paddingInline: space(paddingInline || paddingX || mx),
    paddingInlineEnd: space(paddingInlineEnd || paddingRight),
    paddingInlineStart: space(paddingInlineStart || paddingLeft),
  };
};

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/height
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/overflow
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/width
 */
export const layout = ({
  height,
  maxHeight,
  maxWidth,
  overflow,
  overflowX,
  overflowY,
  width,
}: any) => {
  return {
    height,
    maxHeight,
    maxWidth,
    overflow,
    overflowX,
    overflowY,
    width,
  };
};

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Flexible_Box_Layout/Basic_Concepts_of_Flexbox
 */
export const flexbox = ({
  alignContent,
  alignItems,
  alignSelf,
  flex,
  flexBasis,
  flexDirection,
  flexFlow,
  flexGrow,
  flexShrink,
  flexWrap,
  justifyContent,
  justifySelf,
  order,
  columnGap,
  gap,
  rowGap,
}: any) => ({
  alignContent,
  alignItems,
  alignSelf,
  flex,
  flexBasis,
  flexDirection,
  flexFlow,
  flexGrow,
  flexShrink,
  flexWrap,
  justifyContent,
  justifySelf,
  order,
  columnGap,
  gap,
  rowGap,
});

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout
 */
export const grid = ({
  columnGap,
  gap,
  gridAutoColumns,
  gridAutoFlow,
  gridAutoRows,
  gridColumn,
  gridColumnGap,
  gridGap,
  gridRow,
  gridRowGap,
  gridTemplate,
  gridTemplateAreas,
  gridTemplateColumns,
  gridTemplateRows,
  rowGap,
}: any) => ({
  columnGap,
  gap,
  gridAutoColumns,
  gridAutoFlow,
  gridAutoRows,
  gridColumn,
  gridColumnGap,
  gridGap,
  gridRow,
  gridRowGap,
  gridTemplate,
  gridTemplateAreas,
  gridTemplateColumns,
  gridTemplateRows,
  rowGap,
});

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/position
 */
export const position = ({
  bottom,
  left,
  position: positionProp,
  right,
  top,
}: any) => ({
  bottom,
  left,
  position: positionProp,
  right,
  top,
});

/**
 * Removes undefined values that are added for the supported box CSS
 * properties (above). Without this filter, defined short-hands such as
 * `overflow` will be overruled by `undefined` long-hand values such as
 * `overflowY` or `overflowX`.
 *
 * @see https://webflow.atlassian.net/browse/PDTY-2398
 * @param {Object} styles
 * @returns {Object} styles
 */
// @ts-expect-error - TS7006 - Parameter 'styles' implicitly has an 'any' type.
const filterUndefinedValues = (styles) =>
  pickBy(styles, (value) => typeof value !== 'undefined');

export const boxStyles = ({style = {}, display, ...props}: any = {}) => {
  const baseStyles = {
    boxSizing: 'border-box',
    display,
    ...flexbox(props),
    ...layout(props),
    ...margin(props),
    ...padding(props),
    ...position(props),
  } as const;

  return {
    ...filterUndefinedValues(baseStyles),
    ...style,
  };
};
